import {
  Grid,
  Typography,
  Paper,
  InputLabel,
  TextField,
  FormHelperText,
  useTheme,
} from '@mui/material';
import React, {useCallback, useContext, useEffect, useState} from "react";
import i18next from "i18next";
import _ from "lodash";
import {
  formatIdentifier,
  getCountries,
  getDividedCountries,
  hasOption,
  verifyLuhn,
} from '../../../services/common/Utils';
import useStyles from "../../../style/js-style/containers/thirdparties/TPSearchZoneStyle";
import { Controller, FormProvider, useForm } from 'react-hook-form';
import TpSearchResultTable from "./TPSearchResultTable";
import { Context } from "../../../states/Store";
import { ERROR, SEARCH_TYPE_PATTERNS } from '../../../services/common/Constants';
import { searchThirdparty } from "../../../services/edge/ThirdpartyService";
import EAButton from "../../../components/common/EAButton";
import {useRequestLoading} from "../../../components/common/hooks/useRequestLoading";
import EAAutoCompleteInput from '../../../components/inputs/EAAutoCompleteInput';
import EAIcon from '../../../components/common/EAIcon';
import UnmanagedThirdParty from '../../../components/thirdparties/unmanagedThirdParty/UnmanagedThirdParty';
import {
  getDefaultShownValuesIfExist, getDefaultValuesIfExist,
  unmanagedThirdPartyMode,
} from '../../../components/thirdparties/unmanagedThirdParty/UnmanagedThirdPartyUtils';
import { PrivilegeEnum, RoleBasedAccessControlContext } from '../../../services/common/RolesUtils';
import { Trans } from 'react-i18next';
import EAColorIcon from '../../../components/common/EAColorIcon';
import { getAccountAttributesByBondType } from '../../../services/edge/AttributesService';

const isValidSiret = (value) => {
  return (
    SEARCH_TYPE_PATTERNS["SIRET"].pattern.test(value) && verifyLuhn(value, 14)
  );
};

const isValidSiren = (value) => {
  return (
    SEARCH_TYPE_PATTERNS["SIREN"].pattern.test(value) && verifyLuhn(value, 9)
  );
};

const searchByValueValidator = (value, searchBy, selectedCountry) => {
  switch (searchBy) {
    case "LOCAL_ID":
      let isLocalIdValid;
      if (
        selectedCountry === "FR" &&
        !isValidSiren(value) &&
        !isValidSiret(value)
      ) {
        isLocalIdValid = i18next.t(SEARCH_TYPE_PATTERNS["SIRET"].msg);
      }
      return isLocalIdValid;
    case "DUNS":
      return !SEARCH_TYPE_PATTERNS["DUNS"].pattern.test(value)
        ? i18next.t(SEARCH_TYPE_PATTERNS["DUNS"].msg)
        : undefined;
    case "VAT":
      if (selectedCountry === "FR") {
        return !SEARCH_TYPE_PATTERNS["VAT_FR"].pattern.test(value) &&
          !isValidSiren(value.substring(4))
          ? i18next.t(SEARCH_TYPE_PATTERNS["VAT"].msg)
          : undefined;
      } else {
        return !SEARCH_TYPE_PATTERNS["VAT"].pattern.test(value)
          ? i18next.t(SEARCH_TYPE_PATTERNS["VAT"].msg)
          : undefined;
      }
    default:
      return undefined;
  }
};

function TPSearchZone() {
  const { classes } = useStyles();
  const { isRequestLoading, startLoading, stopLoading } = useRequestLoading();
  const [state, dispatch] = useContext(Context);
  const [results, setResults] = React.useState([]);
  const [favoriteCounties, setFavoriteCounties] = React.useState();
  const [otherCountries, setOtherCountries] = React.useState();
  const [isDnbOrInternationalOn, setIsDnbOrInternationalOn] = React.useState(
    false
  );
  const [hasRestrictedDisclosureTP, setHasRestrictedDisclosureTP] = useState(false);
  const [loader, setLoader] = React.useState(true);
  const [firstClick, setFirstClick] = React.useState(false);
  const [disabledValidate, setDisabledValidate] = useState(true);
  const [isOpenCreateThirdParty, setIsOpenCreateThirdParty] = useState(false);
  const [accountAttributes, setAccountAttributes] = useState([]);
  const { hasAnyOfPrivileges, hasAnyOfPrivilegesOnPropertyId } = React.useContext(RoleBasedAccessControlContext);
  const theme = useTheme();

  const {
    setValue,
    getValues,
    formState: { errors },
    formState,
    handleSubmit,
    control,
    clearErrors,
  } = useForm();
  const formMethods = useForm();

  useEffect(() => {
    setDisabledValidate(!_.isEmpty(formState.errors.searchByValue));
  }, [formState]);

  useEffect(() => {
    setIsDnbOrInternationalOn(
      hasOption(state.accounts, state.account.id, 'DNB') ||
        hasOption(state.accounts, state.account.id, 'INTERNATIONAL')
    );
  }, [state.accounts, state.account]);

  // Récupère la liste des attributs
  const fetchAttributes = useCallback(async () => {
    try {
      const results = await getAccountAttributesByBondType(state.account.id, 'thirdparty', null);
      const mappedAttributes = results.content.map((attribute) => {
        return {
          ...attribute,
          id: attribute.label,
          shownValues: getDefaultShownValuesIfExist(attribute),
          values: getDefaultValuesIfExist(attribute),
          attributeId: attribute.id
        };
      })
      setAccountAttributes(mappedAttributes.sort((a, b) => a.label.localeCompare(b.label)))
    } catch (error) {
      if (error?.message?.messages?.error && error?.message?.messages?.error[0]?.code) {
        dispatch({
          type: "ALERT",
          alert: { type: ERROR, msg: i18next.t(`infos.INTERNAL_ERROR`) }
        });
      }
    }
    // eslint-disable-next-line
  }, [state.account, setValue]);

  useEffect(() => {
    if (state.account) {
      fetchAttributes();
    }
  }, [state.account, fetchAttributes])

  const searchResultsFromApi = useCallback(
    async (data, country) => {
      try {
        startLoading();
        const searchBody = {
          countryCode: country,
          searchBy: data.searchBy,
          value: data.searchByValue,
        };
        let results = await searchThirdparty(
          state.account.id,
          searchBody,
          dispatch
        );
        results = results?.map((r) => {
          return {
            ...r,
            identifier: formatIdentifier(
              r,
              country,
              data.searchBy,
              data.searchByValue
            ),
          };
        });
        const hasRestrictedDisclosureResult = results.find(r => r.hasRestrictedDisclosure === true);
        if (hasRestrictedDisclosureResult) {
          setHasRestrictedDisclosureTP(true);
        }
        setResults(results);
        setLoader(false);
      } catch (error) {
        setResults([]);
        setLoader(false);
      } finally {
        stopLoading();
      }
    },
    [state.account, dispatch, startLoading, stopLoading]
  );

  useEffect(() => {
    let allCountries = getCountries();
    const [favs, others] = getDividedCountries(allCountries);
    setFavoriteCounties(favs);
    setOtherCountries(others);
    setValue("selectedCountry", "FR");
    setValue('searchBy', 'NAME');
    // eslint-disable-next-line
  }, []);

  const handleCountryChange = (value) => {
    setValue("selectedCountry", value ? value.key : null);
  };

  const tpSearchHandler = async (data) => {
    setResults([]);
    setLoader(true);
    setFirstClick(true);
    let dataSearchBy = data.searchBy;
    if (data.selectedCountry === "FR" && data.searchBy === "LOCAL_ID") {
      if (isValidSiret(data.searchByValue)) {
        dataSearchBy = "SIRET";
      } else if (isValidSiren(data.searchByValue)) {
        dataSearchBy = "SIREN";
      }
    }
    data = {
      ...data,
      searchBy: dataSearchBy,
    };
    searchResultsFromApi(data, data.selectedCountry);
  };

  const handleRadioChange = (item) => {
    setValue("searchBy", item ? item.value : null);
    clearErrors("searchByValue");
  };

  const customEAAutoCompleteInputStyle = {
    backgroundColor: "white",
    borderRadius: "12px",
    "& .MuiFormControl-root": {
      "& .MuiAutocomplete-inputRoot": {
        color: "#7E7E8F",
        fontWeight: "600",
        paddingLeft: "5px",
      },
    },
  }

  return (
    <FormProvider {...formMethods}>
      <Paper elevation={0} className={classes.tpSearch}>
        {/* Titre */}
        <div className={classes.title}>
          <div
            className={classes.iconWrapper}
            style={{ backgroundColor: theme.palette.primary.primary200 }}
          >
            <EAColorIcon
              icon="add_green"
              svgStyle={{ position: 'relative', top: '1px', right: '-2px' }}
            />
          </div>
          <Typography variant="h3">
            {i18next.t('addThirdparty.title')}
          </Typography>
        </div>

        <form
          onSubmit={handleSubmit(tpSearchHandler)}
          className={classes.formWrapper}
        >
          {/* Pays */}
          <div className={classes.tpSearchContainer}>
            <InputLabel
              id="countries-select-label"
              className={classes.inputLabel}
            >
              {i18next.t('thirdpartySearch.countrySelect')}
            </InputLabel>
            {favoriteCounties && otherCountries && (
              <EAAutoCompleteInput
                fieldName={'selectedCountry'}
                options={[...favoriteCounties, ...otherCountries]}
                multiple={false}
                disabled={!isDnbOrInternationalOn}
                onChange={handleCountryChange}
                control={control}
                variant="outlined"
                valueName="key"
                defaultValue={'FR'}
                isRequired
                disableClearable
                style={{ ...customEAAutoCompleteInputStyle }}
              />
            )}
          </div>

          {/* Recherche par */}
          <div className={classes.tpSearchContainer}>
            <InputLabel
              id="countries-select-label"
              className={classes.inputLabel}
            >
              {i18next.t('thirdpartySearch.searchBy')}
            </InputLabel>
            {favoriteCounties && otherCountries && (
              <EAAutoCompleteInput
                fieldName={'searchBy'}
                options={[
                  {
                    label: i18next.t(`thirdpartySearch.NAME`),
                    value: 'NAME',
                  },
                  {
                    label: i18next.t(`thirdpartySearch.LOCAL_ID`),
                    value: 'LOCAL_ID',
                  },
                  {
                    label: i18next.t(`thirdpartySearch.VAT`),
                    value: 'VAT',
                  },
                  ...(isDnbOrInternationalOn
                    ? [
                        {
                          label: i18next.t(`thirdpartySearch.DUNS`),
                          value: 'DUNS',
                        },
                      ]
                    : []),
                ]}
                multiple={false}
                onChange={handleRadioChange}
                control={control}
                variant="outlined"
                defaultValue={'NAME'}
                isRequired
                disableClearable
                style={{ ...customEAAutoCompleteInputStyle }}
              />
            )}
          </div>

          {/* Champ de recherche */}
          <div className={classes.tpSearchContainer}>
            <InputLabel
              id="countries-select-label"
              className={classes.inputLabel}
            >
              {' '}
              &nbsp;{' '}
            </InputLabel>
            <Controller
              control={control}
              name="searchByValue"
              defaultValue=""
              rules={{
                required: i18next.t('infos.required'),
                validate: (value) =>
                  searchByValueValidator(
                    value,
                    getValues('searchBy'),
                    getValues('selectedCountry')
                  ),
              }}
              render={({ field }) => (
                <div style={{ position: 'relative' }}>
                  <TextField
                    id="tp-search-input"
                    variant="outlined"
                    error={errors && !!errors.searchByValue}
                    fullWidth
                    sx={{
                      backgroundColor: 'white',
                      borderRadius: '12px',
                      '& .MuiOutlinedInput-root': { border: 'none' },
                    }}
                    {...field}
                  />
                  {!!errors.searchByValue && (
                    <FormHelperText
                      className="Mui-error"
                      style={{ position: 'absolute', top: '37px' }}
                    >
                      {errors.searchByValue.message}
                    </FormHelperText>
                  )}
                </div>
              )}
            />
          </div>

          {/* Soumission */}
          <EAButton
            content={i18next.t('search')}
            className={classes.tpSearchBtn}
            disabled={
              disabledValidate ||
              isRequestLoading ||
              formMethods.formState.errors.selectedCountry ||
              formMethods.formState.errors.searchBy
            }
            validateLoading={isRequestLoading}
            type="submit"
          />
        </form>
      </Paper>

      {/* Résultats */}
      {firstClick && !loader && (
        <div style={{ marginTop: '20px' }}>
          {/* Résultats de recherche titre */}
          <Grid item xs={12} className={classes.resultTitle}>
            <Typography variant="h3">
              {i18next.t('thirdpartySearch.searchResults')}
            </Typography>
            <span className={classes.resultsCounter}>
              {results.length}{' '}
              {i18next.t(results.length < 2 ? 'result' : 'result_plural')}
            </span>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2" className={classes.resultWarning}>
              <span style={{position: "relative", top: "3px", marginRight: "4px"}}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g id="Colored / Info">
                    <path id="Vector" d="M18.3337 10.0013C18.3337 14.6034 14.6024 18.3346 10.0003 18.3346C5.39824 18.3346 1.66699 14.6034 1.66699 10.0013C1.66699 5.39922 5.39824 1.66797 10.0003 1.66797C14.6024 1.66797 18.3337 5.39922 18.3337 10.0013Z" fill="#7E7E8F" />
                    <path id="Vector_2" d="M9.16634 9.16536H10.833V13.7487H9.16634V9.16536ZM11.0413 6.8737C11.0413 7.44828 10.5743 7.91536 9.99967 7.91536C9.42509 7.91536 8.95801 7.44828 8.95801 6.8737C8.95801 6.29911 9.42509 5.83203 9.99967 5.83203C10.5743 5.83203 11.0413 6.29911 11.0413 6.8737Z" fill="#F6F9F3" />
                  </g>
                </svg>
              </span>
              <Trans i18nKey="thirdpartySearch.resultWarning">
                Seuls <b>les établissements actifs</b> sont indiqués.
              </Trans>
            </Typography>
          </Grid>

          {/* Des résultats sont présents. */}
          {!loader && results.length > 0 && (
            <Grid container alignItems="flex-start" spacing={2}>
              <Grid item xs={12} className={classes.tableContainer}>
                <TpSearchResultTable loader={loader} results={results} 
                  accountAttributes={accountAttributes?.filter(attr => 
                    hasAnyOfPrivilegesOnPropertyId(attr.attributeId, PrivilegeEnum.PRIVILEGE_EDIT_THIRD_PARTY_ATTRIBUTE_VALUE))}
                    hasRestrictedDisclosureTP={hasRestrictedDisclosureTP}/>
              </Grid>

              {hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_CREATE_THIRD_PARTY_UNMANAGED) && (
                <Grid item xs={12} className={classes.addNewThirdparty}>
                  <div className={classes.notFoundingThirdParty}>
                    {i18next.t(`thirdpartySearch.notFoundingThirdParty`)}
                  </div>
                  <div className={classes.youCanCreateThirdParty}>
                    {i18next.t(`thirdpartySearch.youCanCreateThirdParty`)}
                  </div>
                  <EAButton
                    content={
                      '+ ' + i18next.t(`thirdpartySearch.createNewThirdParty`)
                    }
                    colorVariant={'primaryLight'}
                    onClick={() => setIsOpenCreateThirdParty(true)}
                    customStyle={{ boxShadow: 'none' }}
                  />
                </Grid>
              )}
            </Grid>
          )}

          {/* Aucun résultats présents. */}
          {!loader && results.length === 0 && (
            <div className={classes.noResultWrapper}>
              <EAIcon icon={'no-result'} style={{ marginBottom: '19px' }} />
              <div className={classes.notFoundingResult}>
                {i18next.t(`thirdpartySearch.notFoundingResult`) +
                  '"' +
                  getValues('searchByValue') +
                  '"'}
              </div>

              {hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_CREATE_THIRD_PARTY_UNMANAGED) && (
                <Grid item xs={12} className={classes.addNewThirdparty}>
                  <div className={classes.youCanCreateThirdParty}>
                    {i18next.t(`thirdpartySearch.youCanCreateThirdParty`)}
                  </div>
                  <EAButton
                    content={
                      '+ ' + i18next.t(`thirdpartySearch.createNewThirdParty`)
                    }
                    colorVariant={'primaryLight'}
                    onClick={() => setIsOpenCreateThirdParty(true)}
                    customStyle={{ boxShadow: 'none' }}
                  />
                </Grid>
              )}
            </div>
          )}
        </div>
      )}

      {/* Création de tiers non géré */}
      {isOpenCreateThirdParty && (
        <UnmanagedThirdParty
          isOpen={isOpenCreateThirdParty}
          onClose={() => setIsOpenCreateThirdParty(false)}
          mode={unmanagedThirdPartyMode.CREATE}
        />
      )}
    </FormProvider>
  );
}

export default TPSearchZone;
